import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { CloudSun, Sunrise, Sunset, Wind, Droplets, Loader2 } from "lucide-react";

// A compact "Weather on this day" panel. Renders nothing while loading
// or when the upstream fails (fail-soft).
//
// Props:
//   date      — ISO YYYY-MM-DD (e.g. death_date or event date)
//   lat, lon  — optional coordinates. Preferred over `location`.
//   location  — free-text place; server geocodes via Nominatim.
//   label     — heading label ("Weather on the day of passing", etc.)
export default function WeatherOnThisDay({ date, lat, lon, location, label }) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    if (!date || !/^\d{4}-\d{2}-\d{2}/.test(String(date).slice(0, 10))) {
      setState({ loading: false, data: null });
      return;
    }
    // Also bail if there's no location signal at all.
    if (lat == null && lon == null && !location) {
      setState({ loading: false, data: null });
      return;
    }
    let cancelled = false;
    setState({ loading: true, data: null });
    const params = { date: String(date).slice(0, 10) };
    if (lat != null && lon != null) { params.lat = lat; params.lon = lon; }
    else if (location) { params.location = location; }
    http.get("/weather/on-this-day", { params }).then((r) => {
      if (cancelled) return;
      setState({ loading: false, data: r.data && r.data.ok ? r.data : null });
    }).catch(() => {
      if (cancelled) return;
      setState({ loading: false, data: null });
    });
    return () => { cancelled = true; };
  }, [date, lat, lon, location]);

  if (state.loading) return null; // silent while fetching
  if (!state.data || !state.data.day) return null;

  const d = state.data.day;
  const tempDay = d.temp?.day ?? d.temp?.max;
  return (
    <div
      className="wofy-card"
      data-testid="weather-on-this-day"
      style={{ background: "linear-gradient(135deg, #FEF3C7 0%, #ECFDF5 100%)" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <CloudSun className="w-4 h-4" style={{ color: "#B45309" }} />
        <div className="overline" style={{ color: "#B45309" }}>{label || "Weather on this day"}</div>
      </div>
      <div className="text-sm font-display leading-snug mb-3">
        {d.summary
          ? d.summary.charAt(0).toUpperCase() + d.summary.slice(1)
          : "Weather record found"}
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
        {tempDay != null && (
          <div className="flex items-center gap-1.5"><span className="font-mono text-sm" style={{ color: "var(--wofy-text)" }}>{Math.round(tempDay)}°C</span> daytime</div>
        )}
        {d.wind_speed != null && (
          <div className="flex items-center gap-1.5"><Wind className="w-3 h-3" /> {Math.round(d.wind_speed)} m/s wind</div>
        )}
        {d.precipitation != null && (
          <div className="flex items-center gap-1.5"><Droplets className="w-3 h-3" /> {Number(d.precipitation).toFixed(1)} mm rain</div>
        )}
        {d.sunrise && (
          <div className="flex items-center gap-1.5"><Sunrise className="w-3 h-3" /> {d.sunrise}</div>
        )}
        {d.sunset && (
          <div className="flex items-center gap-1.5"><Sunset className="w-3 h-3" /> {d.sunset}</div>
        )}
      </div>
      <div className="mt-3 text-[10px] font-mono uppercase tracking-wide opacity-50">
        {state.data.attribution}
      </div>
    </div>
  );
}
